import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconPackage, IconPlus } from "@tabler/icons-react";
import type { ProductDto } from "../../../../api";
import {
  ProductRequestDtoStatusEnum,
  type ProductsQueryDto,
} from "../../../../api";
import { Heading } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { EmptyState } from "../../../../components/common/empty-state";
import { SearchBar } from "../../../../components/common/input";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { SimpleTable } from "../../../../components/common/simple-table";
import { Pagination } from "../../../../components/common/pagination";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { useMyProducts } from "../../hooks/useMyProducts";
import {
  useProductLifecycleActions,
  type ProductLifecycleAction,
} from "../../hooks/useProductLifecycleActions";
import { useProductRequests } from "../../../product-requests/hooks/useProductRequests";
import { useMyPendingProductChangeRequests } from "../../../change-requests/hooks/useMyPendingProductChangeRequests";
import { usePaginationSortingUrlSync } from "../../../pagination/hooks/usePaginationSortingUrlSync";
import { useSearchFiltersUrlSync } from "../../../pagination/hooks/useSearchFiltersUrlSync";
import { AppRoutes } from "../../../../common/routes/app_routes";
import {
  buildProductColumns,
  buildRequestColumns,
} from "../components/my-products-columns";
import { useCategoryLabels } from "../../hooks/useCategoryLabels";
import { ProductStatusDialog } from "../components/product-status-dialog";

type Tab = "products" | "requests";
const TAB_OPTIONS: { value: Tab; label: string }[] = [
  { value: "products", label: "Products" },
  { value: "requests", label: "Product Requests" },
];
const VALID_TABS = new Set<Tab>(TAB_OPTIONS.map((o) => o.value));
const DEFAULT_TAB: Tab = "products";

export function MyProductsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { updateLifecycle, isSaving } = useProductLifecycleActions();
  const [pendingLifecycle, setPendingLifecycle] = useState<{
    product: ProductDto;
    action: ProductLifecycleAction;
  } | null>(null);

  const tabParam = searchParams.get("tab") as Tab | null;
  const tab: Tab =
    tabParam && VALID_TABS.has(tabParam) ? tabParam : DEFAULT_TAB;
  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    if (next === DEFAULT_TAB) params.delete("tab");
    else params.set("tab", next);
    // Switching tabs resets pagination so the URL doesn't carry stale ?page=N
    // from the previous list.
    params.delete("page");
    setSearchParams(params, { replace: false });
  };

  const { searchValue, setSearchValue, baseQuery } = useSearchFiltersUrlSync<ProductsQueryDto>({
    initialFilters: {} as Record<string, unknown>,
    enableSearch: true,
  });

  const { pagination, sortingQuery } = usePaginationSortingUrlSync({
    defaultPageSize: 10,
  });

  const products = useMyProducts({
    ...baseQuery,
    ...sortingQuery,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  } as ProductsQueryDto);

  const requests = useProductRequests({
    ...baseQuery,
    ...sortingQuery,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  } as ProductsQueryDto);

  // CLAUDE.md rule #7 — codegen collision: the OpenAPI generator types the
  // getAllProductRequests param as the generic `ProductsQueryDto`, which lacks
  // `requestStatus` (the correct shape, `ProductRequestQueryDto`, is generated
  // but not referenced by the method). Narrow cast until the BE disambiguates
  // the operation so codegen emits the right param type.
  const pendingRequests = useProductRequests({
    page: 1,
    pageSize: 1,
    requestStatus: ProductRequestDtoStatusEnum.Pending,
  } as unknown as ProductsQueryDto, { excludeDrafts: true });
  const visibleRequests = requests.data?.data ?? [];
  const { data: pendingCrs } = useMyPendingProductChangeRequests();
  const pendingReviewIds = pendingCrs?.ids;
  const productColumns = useMemo(
    () =>
      buildProductColumns(
        (product, action) => {
          setPendingLifecycle({ product, action });
        },
        pendingReviewIds,
      ),
    [pendingReviewIds],
  );
  const categoryLabel = useCategoryLabels();
  const requestColumns = useMemo(() => buildRequestColumns(categoryLabel), [categoryLabel]);

  async function confirmLifecycleAction() {
    if (!pendingLifecycle) return;
    try {
      await updateLifecycle(pendingLifecycle.product.id, pendingLifecycle.action);
      setPendingLifecycle(null);
    } catch {
      // Toast already shown by the hook; keep the dialog open for a retry.
    }
  }

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">
      <Heading variant="h2" tone="dark">
        Products
      </Heading>

      <div className="grid grid-cols-2 gap-4 max-md:gap-3 lg:grid-cols-4">
        <AccentStatCard
          label="Total products"
          value={products.data?.meta?.totalCount ?? 0}
        />
        <AccentStatCard
          label="Pending approval"
          value={pendingRequests.data?.meta?.totalCount ?? 0}
        />
        {/* Out-of-stock + Categories aren't derivable from current APIs.
            Rendering '—' until backend exposes a stock summary on ProductDto
            and a category-aggregate endpoint. */}
        <AccentStatCard label="Out of stock" value="—" accent="amber" />
        <AccentStatCard label="Categories" value="—" />
      </div>

      <div className="flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-3">
        <Heading variant="h3" tone="dark">
          All products
        </Heading>
        <div className="flex items-center gap-3 max-md:flex-col max-md:items-stretch">
          <Button
            variant="primary"
            size="sm"
            leftIcon={<IconPlus size={16} />}
            onClick={() => navigate(AppRoutes.MY_PRODUCT_BULK_UPLOAD)}
            className="!px-5"
          >
            Add Products in Bulk
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<IconPlus size={16} />}
            onClick={() => navigate(AppRoutes.MY_PRODUCT_NEW)}
            className="!px-5"
          >
            Add Product Manually
          </Button>
        </div>
      </div>

      <div className="flex items-stretch gap-3 max-md:flex-col">
        <div className="flex-1 max-md:flex-none">
          <SearchBar
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search"
            wrapperClassName="h-full"
          />
        </div>
        <SegmentedControl
          options={TAB_OPTIONS}
          value={tab}
          onChange={setTab}
          className="max-md:self-start"
        />
      </div>

      {tab === "products" ? (
        products.isLoading ? (
          <ShimmerLoader message="Fetching your products…" lines={6} />
        ) : (products.data?.data ?? []).length === 0 ? (
          <EmptyState
            icon={IconPackage}
            title="You have no products yet."
            description="Add your first product to start selling on Procurtal."
            primaryAction={{
              label: "Add Product Manually",
              leftIcon: <IconPlus size={16} />,
              onClick: () => navigate(AppRoutes.MY_PRODUCT_NEW),
            }}
            secondaryAction={{
              label: "Add Products in Bulk",
              leftIcon: <IconPlus size={16} />,
              onClick: () => navigate(AppRoutes.MY_PRODUCT_BULK_UPLOAD),
            }}
            className="rounded-2xl border border-borderDefault bg-bgWhite"
          />
        ) : (
          <SimpleTable
            columns={productColumns}
            data={products.data?.data ?? []}
            keyExtractor={(p) => String(p.id)}
            onRowClick={(p) =>
              navigate(AppRoutes.MY_PRODUCT_DETAILS.replace(":id", String(p.id)))
            }
            footer={
              (products.data?.meta?.pageCount ?? 1) > 1 ? (
                <PaginationFooter pageCount={products.data?.meta?.pageCount ?? 1} />
              ) : undefined
            }
          />
        )
      ) : requests.isLoading ? (
        <ShimmerLoader message="Fetching product requests…" lines={6} />
      ) : visibleRequests.length === 0 ? (
        <EmptyState
          icon={IconPackage}
          title="You have no product requests."
          description="Submit a new product to get it reviewed and published. Your pending submissions will appear here."
          primaryAction={{
            label: "Add Product Manually",
            leftIcon: <IconPlus size={16} />,
            onClick: () => navigate(AppRoutes.MY_PRODUCT_NEW),
          }}
          secondaryAction={{
            label: "Add Products in Bulk",
            leftIcon: <IconPlus size={16} />,
            onClick: () => navigate(AppRoutes.MY_PRODUCT_BULK_UPLOAD),
          }}
          className="rounded-2xl border border-borderDefault bg-bgWhite"
        />
      ) : (
        <SimpleTable
          columns={requestColumns}
          data={visibleRequests}
          keyExtractor={(r) => String(r.id)}
          onRowClick={(r) =>
            navigate(
              r.isDraft
                ? AppRoutes.MY_PRODUCT_REQUEST_EDIT.replace(":id", String(r.id))
                : AppRoutes.PRODUCT_REQUEST_DETAILS.replace(":id", String(r.id)),
            )
          }
          footer={
            (requests.data?.meta?.pageCount ?? 1) > 1 ? (
              <PaginationFooter pageCount={requests.data?.meta?.pageCount ?? 1} />
            ) : undefined
          }
        />
      )}

      <ProductStatusDialog
        open={pendingLifecycle !== null}
        action={pendingLifecycle?.action ?? null}
        productName={pendingLifecycle?.product.displayName ?? pendingLifecycle?.product.name ?? "this product"}
        isSaving={isSaving}
        onConfirm={() => void confirmLifecycleAction()}
        onClose={() => setPendingLifecycle(null)}
      />
    </div>
  );
}

function PaginationFooter({ pageCount }: { pageCount: number }) {
  const params = new URLSearchParams(window.location.search);
  const page = Number(params.get("page") ?? "1") || 1;
  return (
    <div className="flex justify-center">
      <Pagination
        page={page}
        pageCount={pageCount}
        onPageChange={(next) => {
          const url = new URL(window.location.href);
          url.searchParams.set("page", String(next));
          window.history.replaceState(null, "", url.toString());
          window.dispatchEvent(new PopStateEvent("popstate"));
        }}
      />
    </div>
  );
}

export default MyProductsPage;
